import {
  mapOverTree,
  mapOverTreeFromLeafs,
  appendLeafToTree,
} from 'utils/tree';

import {
  patchKpiStatus,
} from 'utils/kpi';

import objectFilter from 'utils/objectFilter';

const TARGET = 'data_categories';
const KPI_TARGET = 'data_kpi';
const META_TARGET = 'data_meta';
const APPROVAL_TARGET = 'data_approval';
const KPI_MONITORING = 'kpi_monitor';

const SKIP_META_TARGETS = ['descendants'];

const isTarget = (target) => [
  TARGET,
  KPI_TARGET,
  META_TARGET,
  APPROVAL_TARGET,
  KPI_MONITORING,
].includes(target);
const isGeneralTarget = (target) => target === TARGET;

const ID_FIELD = 'slug';
const CHILD_FIELD_FOR_PARENT = 'category_slug';
const PREV_ID_FIELD = 'old_slug';

const initialState = {
  data: null,
  tags: [],
  loading: false,
  error: null,
};

const getTags = (categories) => {
  const getTagsRepeated = (tree) => 
    tree.reduce((acc, node) => node.children 
      ? [...acc, ...getTagsRepeated(node.kpis || []), ...getTagsRepeated(node.children || [])]
      : [...acc, ...(node.tags || [])], []);
    
  return Array.from(new Set(getTagsRepeated(categories)))
}

const reducer = (state = initialState, action) => {
  let data, entry;
  switch(action.type) {
    case 'API_CALL_REQUEST':
      if(!isTarget(action.target)) return state;
      return {
        data: action.method === 'GET' ? null : state.data,
        tags: action.method === 'GET' ? [] : state.tags,
        loading: true,
        error: null,
      };
    case 'API_CALL_COMPLETE':
      if(!action.response || !isTarget(action.response.target)) return state;
      data = state.data || [];
      if(isGeneralTarget(action.response.target)) {
        switch(action.response.method) {
          case 'GET':
            // Categories obtained
            data = mapOverTree(action.response.result)((node) => {
              return {
                ...node,
                kpis: (node.kpis || []).map(patchKpiStatus),
              }
            });
            break;
          case 'POST':
            // Category created
            if(!action.response.result.length) {
              entry = {
                ...(action.response.result),
                kpis: [],
                esg_types: [],
                sdgs: [],
                standards: [],
                is_custom: true,
              };
              data = !action.response.body.parent_slug
                ? data.concat(entry)
                : appendLeafToTree(data)(
                  (node) => (
                    node.slug === action.response.body.parent_slug
                  ),
                  entry,
                );

            } else {
              data = action
                .response
                .result
                .map(c => ({
                  ...c,
                  kpis: [],
                  esg_types: [],
                  sdgs: [],
                  standards: [],
                  is_custom: true
                })).reduce((acc, newCategory) =>
                    !newCategory.parent
                      ? acc.concat(newCategory)
                      : appendLeafToTree(acc)(
                        (node) => (
                          node.slug === newCategory.parent
                        ),
                        newCategory,
                      )
                  , data)
            }
            break;
          case 'PUT':
            // Category updated
            data = mapOverTree(data)((node) => {
              if(node[ID_FIELD] === action.response.result[PREV_ID_FIELD]) {
                return {
                  ...node,
                  ...action.response.result,
                };
              }
              return node;
            })
            break;
          case 'DELETE':
            data = mapOverTreeFromLeafs(data)((node, children) => 
             { 
              if (node.slug === action.response.result[ID_FIELD]) return null
              return {
              ...node,
              children: children.filter(category => category.slug !== action.response.result[ID_FIELD])
            }});
            break;
          default:
        }
        return {
          data,
          tags: getTags(data),
          loading: false,
          error: null,
        };
      }

      if(action.response.target === KPI_TARGET) {
        if(action.response.result) {
          // Category updated
          entry = action.response.result;
          data = mapOverTree(data)((node) => {
            if(
              action.response.method === 'POST' &&
              node[ID_FIELD] === entry[CHILD_FIELD_FOR_PARENT]
            ) {
              const kpis = [
                ...(node.kpis || []),
                {
                  ...(
                    objectFilter([
                      'name',
                      'slug',
                      'tags',
                      'type',
                      'applies',
                      'kpi_value',
                      'standards',
                      'standards_info',
                      'previous_kpi_value',
                      'sdgs',
                    ])(entry)
                  ),
                  period: (entry.period || {}).label,
                  periods: (entry.all_periods || []),
                  status: 'pending',
                },
              ];
              return {
                ...node,
                applies: true,
                kpis,
              };
            }
            if(
              action.response.method === 'PUT' &&
              (node.kpis || []).find(kpi => kpi.slug === entry.slug)
            ) {
              const kpis = (node.kpis || []).map(
                kpi => {
                  if(kpi.slug === entry.slug && kpi.periods) {
                    return {
                      ...kpi,
                      periods: kpi.periods.map(
                        period => {
                          if(period.period === entry.period.label) {
                            return {
                              ...period,
                              kpi_value: entry.kpi_value,
                              rejection_count: 0,
                            };
                          }
                          return period
                        }
                      )
                    };
                  }
                  return kpi;
                }
              );
              return {
                ...node,
                kpis,
              };
            }
            return node;
          })
        }
        return {
          data,
          tags: state.tags,
          loading: false,
          error: null,
        };
      }

      if(action.response.target === KPI_MONITORING) {
        if(action.response.result) {
          // Category updated
          entry = action.response.result;
          data = mapOverTree(data)((node) => {
            if(
              (node.kpis || []).find(kpi => kpi.slug === entry.slug)
            ) {
              const kpis = (node.kpis || []).map(
                kpi => {
                  if(kpi.slug === entry.slug && kpi.periods && entry.period) {
                    return {
                      ...kpi,
                      periods: kpi.periods.map(
                        period => {
                          if(period.period === entry.period) {
                            return {
                              ...period,
                              monitoring_value: entry.monitoring_value.filter(
                                el => el.period === period.period
                              )
                            };
                          }
                          return period
                        }
                      )
                    };
                  }else if (kpi.slug === entry.slug && kpi.periods){
                    return {
                      ...kpi,
                      periods: kpi.periods.map(
                        period => {
                            return {
                              ...period,
                              monitoring_value: entry.monitoring_value.filter(
                                el => el.period === period.period
                              )
                            };
                        }
                      )
                    };
                  }
                  return kpi;
                }
              );
              return {
                ...node,
                kpis,
              };
            }
            return node;
          })
        }
        return {
          data,
          loading: false,
          error: null,
        };
      }

      if(action.response.target === APPROVAL_TARGET) {
        if(action.response.result && Array.isArray(action.response.result)) {
          const slugMap = new Map(
            action.response.result.map(kpi => [kpi.slug, kpi])
          );
          data = mapOverTree(data)((node) => {
            return {
              ...node,
              kpis: (node.kpis || []).map(
                kpi => {
                  if(slugMap.has(kpi.slug) && kpi.periods) {
                    entry = slugMap.get(kpi.slug)
                    return {
                      ...kpi,
                      periods: kpi.periods.map(
                        period => {
                          if(period.period === entry.period.label) {
                            return {
                              ...period,
                              ready_to_validate_level: entry.ready_to_validate_level,
                              validated_level: entry.validated_level,
                              rejection_count: entry.rejections?.length || 0,
                              rejection_comment: entry.rejections?.length
                                ? entry.rejections[0].comment
                                : null
                            };
                          }
                          return period
                        }
                      )
                    };
                  }
                  return kpi;
                }
              ),
            };
          });
        }
        return {
          data,
          tags: state.tags,
          loading: false,
          error: null,
          showValidationChangedNotification: true,
        };
      }

      if(action.response.target === META_TARGET) {
        if(
          !action.response.result ||
          !action.response.result.kpi_slugs ||
          SKIP_META_TARGETS.includes(action.response.meta_target) ||
          !action.response.organization_slug ||
          !action.response.suborganization_slug
          // NOTICE: Prevent from reacting to META_TARGET response if this org was not affected
        ) {
          return {
            data,
            tags: state.tags,
            loading: false,
            error: null,
          }
        }

        const {
          kpi_slugs = [],
          add_tags,
          ...updatedValues
        } = action.response.result;

        const newData = mapOverTree(data)(node => {
          const kpis = node.kpis.map(kpi => {
            if (kpi_slugs.includes(kpi.slug)) {
              return {
                ...kpi,
                ...updatedValues,
                tags: add_tags || kpi.tags,
              };
            }
            return kpi;
          });
          return { ...node, kpis };
        });

        return {
          data: newData,
          tags: state.tags,
          loading: false,
          error: null,
        };
      }

      return {
        data,
        tags: state.tags,
        loading: false,
        error: null,
      };
    case 'API_CALL_FAILED':
      if(!action.request || !isTarget(action.request.target)) return state;
      return {
        ...state,
        loading: false,
        error: action.code,
      };
    case 'RESET_SHOW_VALIDATION_NOTIFICATION':
      return {
        ...state,
        showValidationChangedNotification: false,
      };
    case 'RESET_AUTH':
    case 'LOGOUT_REQUEST':
      return initialState;
    default:
      return state;
  }
};

export {
  reducer as data_categories,
};
